/**
 * Quiz design system — single source of truth for the self-service assessment UI.
 *
 * Primary:  #ffdb00 (yellow)  #000000 (black)  #ffffff (white)
 * Accents:  #4061c2  #fd635e  #19c986  #fa93b8  #2daae1  #007881  #8967c8  #ff8200
 *
 * All class strings are complete (never dynamically concatenated) so Tailwind JIT
 * can detect them via static analysis of this file.
 */

// ── Raw color values (for inline styles / documentation) ─────────────────────

export const qcolors = {
  yellow:  '#ffdb00',
  black:   '#000000',
  white:   '#ffffff',
  blue:    '#4061c2',
  red:     '#fd635e',
  green:   '#19c986',
  pink:    '#fa93b8',
  lblue:   '#2daae1',
  teal:    '#007881',
  purple:  '#8967c8',
  orange:  '#ff8200',
} as const

// ── Font roles ───────────────────────────────────────────────────────────────
// Headings: Clancy  |  Body copy: Roboto  |  Supporting: Arial

export const qfonts = {
  heading:    'font-quiz-heading',
  body:       'font-quiz-body',
  supporting: 'font-quiz-supporting',
} as const

// ── Reusable Tailwind class strings ──────────────────────────────────────────

export const qcls = {
  // Inputs & labels
  label: 'block text-xs font-bold uppercase tracking-widest text-black mb-2',
  input: [
    'w-full px-4 py-3 border-2 border-gray-200 bg-white text-black text-sm',
    'placeholder:text-gray-400 placeholder:font-quiz-body placeholder:text-base',
    'focus:outline-none focus:border-brand-yellow',
    'transition-colors',
  ].join(' '),

  // Buttons
  btnPrimary: [
    'w-full bg-brand-yellow text-black font-black text-sm py-4 rounded-lg',
    'hover:brightness-95 active:scale-[0.99]',
    'disabled:opacity-40 disabled:cursor-not-allowed',
    'transition-all uppercase tracking-wider',
    'flex items-center justify-center gap-2',
  ].join(' '),
  btnSecondary: [
    'px-6 py-3 border-2 border-brand-yellow bg-brand-yellow text-black text-sm font-bold rounded-lg',
    'hover:brightness-95 active:scale-[0.99]',
    'transition-all uppercase tracking-wider',
  ].join(' '),

  // Feedback
  errorBox: [
    'flex items-center gap-2',
    'py-2 font-[Roboto,sans-serif] text-base text-[#F90909]',
  ].join(' '),

  // Progress bar
  progressTrack: 'w-full h-1 bg-white/20 rounded-full overflow-hidden',
  progressFill: 'h-full bg-brand-yellow rounded-full transition-all duration-500',

  // Spinner
  spinner: 'animate-spin w-4 h-4',
} as const

// ── Tag helpers (level / outcome-type) ───────────────────────────────────────
// Must return full, static class strings so Tailwind JIT can detect them.

const LEVEL_TAG: Record<string, string> = {
  'Basic':           'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-acc-blue/10 text-acc-blue',
  'Intermediate':    'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-acc-teal/10 text-acc-teal',
  'Advanced':        'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-acc-purple/10 text-acc-purple',
  'Highly advanced': 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-acc-orange/10 text-acc-orange',
}
const TYPE_TAG: Record<string, string> = {
  'Knowledge': 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-acc-lblue/10 text-acc-lblue',
  'Skill':     'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-acc-green/10 text-acc-green',
  'Attitude':  'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-acc-pink/10 text-acc-pink',
}
const FALLBACK_TAG = 'inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600'

export const levelTagCls  = (l: string) => LEVEL_TAG[l]  ?? FALLBACK_TAG
export const typeTagCls   = (t: string) => TYPE_TAG[t]   ?? FALLBACK_TAG
